import { Center, Group, Stack } from '@mantine/core';
import { Button, Icon, Text, Title, type IconNames } from '@repo/foundations';
import { space } from '@repo/theme/primitives';
import type { AnimatedIllustrationGIFNames } from '../Illustration/AnimatedIllustration';
import { AnimatedIllustration } from '../Illustration/AnimatedIllustration';
import type { IllustrationNames } from '../Illustration/constants';
import { Illustration } from '../Illustration/Illustration';

interface EmptyStateButton {
	label: string;
	variant: 'primary' | 'default';
	onClick: () => void | Promise<void>;
	requiresUseButtonPermission?: boolean;
}

export type EmptyStateButtons =
	| [EmptyStateButton]
	| [EmptyStateButton, EmptyStateButton];

type EmptyStateProps = {
	body: string;
	heading?: string;
	buttons?: EmptyStateButtons;
	iconName?: IconNames;
	illustrationName?: IllustrationNames;
	animatedIllustrationName?: AnimatedIllustrationGIFNames;
	hasUseButtonPermission?: boolean;
};

export function EmptyState({
	body,
	iconName,
	illustrationName,
	animatedIllustrationName,
	heading,
	buttons,
	hasUseButtonPermission,
}: EmptyStateProps) {
	// Filter out buttons that require permission if user does not have permission
	const filteredButtons = buttons?.filter(
		(button) => !(button.requiresUseButtonPermission && !hasUseButtonPermission)
	);

	return (
		<Center w="100%" h="100%" data-testid="empty-state">
			<Stack w={space[100]} spacing="md" align="center">
				{iconName && (
					<Icon name={iconName} size="lg" color="icon/secondary/default" />
				)}
				{illustrationName && (
					<Illustration name={illustrationName} size={space[20]} />
				)}
				{animatedIllustrationName && (
					<AnimatedIllustration
						name={animatedIllustrationName}
						size={space[20]}
					/>
				)}
				<Stack spacing={0} align="center">
					{heading && <Title size="md">{heading}</Title>}
					<Text size="sm" color="text/secondary/default" align="center">
						{body}
					</Text>
				</Stack>
				{filteredButtons && (
					<Group spacing="xs" position="center">
						{filteredButtons.map((button) => (
							<Button
								key={button.label}
								variant={button.variant}
								onClick={button.onClick}
							>
								{button.label}
							</Button>
						))}
					</Group>
				)}
			</Stack>
		</Center>
	);
}
